import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  type TooltipContentProps,
} from "recharts";
import type { WinLosePoint } from "@shared-core/data/api-types";
import { niceAxisMax } from "@shared-core/domain/insights-monthly-revenue";
import {
  overallWinRatePct,
  projectWinLose,
  type WinLoseDatum,
} from "@shared-core/domain/insights-sales-mix";

// Quote win/lose counts (stacked bars, left axis) with the win-rate line
// (right axis, won / (won + lost) — undecided quotes excluded). Server
// composes (quoted_value_monthly's status-filtered counts), client only
// derives the rate and renders.

const WON_COLOR = "var(--color-chart-2)";
const LOST_COLOR = "#ef4444";
const RATE_COLOR = "var(--color-chart-1)";

export interface WinLoseChartProps {
  points: WinLosePoint[];
}

export function WinLoseChart({ points }: WinLoseChartProps) {
  const data = projectWinLose(points);

  if (data.length === 0) {
    return (
      <div className="border-border bg-surface text-muted-foreground rounded-lg border p-12 text-center text-sm">
        No quote data for this period yet.
      </div>
    );
  }

  const countMax = niceAxisMax(
    Math.max(0, ...data.map((d) => d.wonCount + d.lostCount)),
  );
  const overallRate = overallWinRatePct(data);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-foreground text-base font-semibold">
            Quote win/lose rate — monthly
          </h2>
          <p className="text-muted-foreground text-sm">
            Whole company · trailing {data.length} months
          </p>
        </div>
        <div className="border-border bg-surface rounded-lg border px-3 py-2 text-right">
          <div className="text-muted-foreground text-xs font-medium">
            Overall win rate
          </div>
          <div className="text-foreground font-mono text-xl font-semibold tabular-nums">
            {overallRate.toFixed(1)}%
          </div>
        </div>
      </div>

      <div className="border-border bg-surface rounded-lg border p-4">
        <ChartLegend />
        <ResponsiveContainer width="100%" height={360}>
          <ComposedChart
            data={data}
            margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
          >
            <CartesianGrid stroke="var(--color-border)" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fontSize: 11, fill: "var(--color-muted-foreground)" }}
              axisLine={{ stroke: "var(--color-border)" }}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={16}
            />
            <YAxis
              yAxisId="count"
              domain={[0, countMax]}
              allowDataOverflow
              tickCount={5}
              tick={{ fontSize: 10, fill: "var(--color-muted-foreground)" }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <YAxis
              yAxisId="rate"
              orientation="right"
              domain={[0, 100]}
              tickCount={5}
              tickFormatter={(v: number) => `${v}%`}
              tick={{ fontSize: 10, fill: RATE_COLOR }}
              axisLine={false}
              tickLine={false}
              width={40}
            />
            <Tooltip
              cursor={{ fill: "var(--color-muted)", opacity: 0.3 }}
              content={WinLoseTooltip as never}
            />
            <Bar
              yAxisId="count"
              dataKey="wonCount"
              name="Won"
              stackId="quotes"
              fill={WON_COLOR}
              radius={[2, 2, 0, 0]}
              isAnimationActive={false}
              fillOpacity={0.95}
            />
            <Bar
              yAxisId="count"
              dataKey="lostCount"
              name="Lost"
              stackId="quotes"
              fill={LOST_COLOR}
              isAnimationActive={false}
              fillOpacity={0.85}
            />
            <Line
              yAxisId="rate"
              dataKey="winRatePct"
              name="Win rate"
              stroke={RATE_COLOR}
              strokeWidth={2.4}
              dot={{ r: 2.6, fill: RATE_COLOR, strokeWidth: 0 }}
              connectNulls
              isAnimationActive={false}
            />
          </ComposedChart>
        </ResponsiveContainer>
        <p className="text-muted-foreground mt-3 text-xs">
          Win rate = won ÷ (won + lost); open/superseded/stale enquiries are
          excluded — undecided, not a loss.
        </p>
      </div>
    </div>
  );
}

function ChartLegend() {
  return (
    <div className="text-muted-foreground mb-3 flex flex-wrap items-center gap-4 text-xs">
      <LegendSwatch color={WON_COLOR} label="Won" />
      <LegendSwatch color={LOST_COLOR} label="Lost" />
      <LegendLine color={RATE_COLOR} label="Win rate (right axis)" />
    </div>
  );
}

function LegendSwatch({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-3 w-3 rounded-sm"
        style={{ backgroundColor: color }}
      />
      {label}
    </span>
  );
}

function LegendLine({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span
        className="inline-block h-0 w-4"
        style={{ borderTop: `3px solid ${color}` }}
      />
      {label}
    </span>
  );
}

function WinLoseTooltip(props: TooltipContentProps<number, string>) {
  if (!props.active || !props.payload || props.payload.length === 0) {
    return null;
  }
  const datum = props.payload[0]?.payload as WinLoseDatum | undefined;
  if (!datum) return null;
  return (
    <div
      className="border-border bg-popover text-popover-foreground rounded-lg border px-3 py-2 text-xs shadow-lg"
      role="tooltip"
    >
      <div className="mb-1 font-semibold">{datum.label}</div>
      <TooltipRow
        color={WON_COLOR}
        label="Won"
        value={String(datum.wonCount)}
      />
      <TooltipRow
        color={LOST_COLOR}
        label="Lost"
        value={String(datum.lostCount)}
      />
      <div className="text-muted-foreground mt-0.5">
        {datum.quoteCount} quotes total ·{" "}
        {datum.winRatePct === null
          ? "no decisions yet"
          : `${datum.winRatePct.toFixed(1)}% win rate`}
      </div>
    </div>
  );
}

function TooltipRow({
  color,
  label,
  value,
}: {
  color: string;
  label: string;
  value: string;
}) {
  return (
    <div className="flex items-center justify-between gap-4">
      <span className="inline-flex items-center gap-1.5">
        <span
          className="inline-block h-2 w-2 rounded-sm"
          style={{ backgroundColor: color }}
        />
        {label}
      </span>
      <span className="font-mono tabular-nums">{value}</span>
    </div>
  );
}
